
import React, { useState, useEffect } from 'react';
import { 
  Box, Typography, Button, Paper, Grid, Card, CardContent, Dialog, DialogTitle, 
  DialogContent, DialogActions, Select, MenuItem, FormControl, InputLabel, 
  List, ListItem, ListItemText, ListItemAvatar, Avatar, IconButton, TextField, Autocomplete,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, InputAdornment, Grow, Stack, Divider
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import VisibilityIcon from '@mui/icons-material/Visibility';
import axios from 'axios';
import API_URL from '../config';
import { MapContainer, TileLayer, Marker, useMapEvents, Popup } from 'react-leaflet';
import L from 'leaflet';
import { useNavigate } from 'react-router-dom';

// Fix for default marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

function LocationMarker({ position, setPosition }) {
  const map = useMapEvents({
    click(e) {
      if (setPosition) {
          setPosition(e.latlng);
          map.flyTo(e.latlng, map.getZoom());
      }
    },
  });

  return position ? <Marker position={position} /> : null;
}

function Punching() {
  const [locations, setLocations] = useState([]);
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  
  const [selectedUser, setSelectedUser] = useState(null);
  const [newLocation, setNewLocation] = useState({ lat: 25.0, lng: 45.0 }); // Default
  const [address, setAddress] = useState('');
  const [userPunchingId, setUserPunchingId] = useState('');

  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = async () => {
    try {
      const [locRes, userRes] = await Promise.all([
        axios.get(`${API_URL}/punching/locations`, { headers }),
        axios.get(`${API_URL}/punching/users`, { headers })
      ]);
      setLocations(locRes.data);
      setUsers(userRes.data);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleSave = async () => {
    if (!selectedUser) return alert('Select a user');
    setLoading(true);
    try {
        await axios.post(`${API_URL}/punching/locations`, {
            user_id: selectedUser.id,
            latitude: newLocation.lat,
            longitude: newLocation.lng,
            address: address,
            userpunchingid: userPunchingId
        }, { headers });
        setOpen(false);
        fetchData();
        setSelectedUser(null);
        setAddress('');
        setUserPunchingId('');
    } catch (err) {
        alert('Error saving location');
    } finally {
        setLoading(false);
    }
  };

  const handleDelete = async (id) => {
      if(!window.confirm('Delete this location assignment?')) return;
      try {
          await axios.delete(`${API_URL}/punching/locations/${id}`, { headers });
          fetchData();
      } catch (err) {
          alert('Error deleting');
      }
  };

  const handleUserClick = (userId) => {
      navigate(`/punching/${userId}`);
  };

  const filteredLocations = locations.filter(loc => 
      (loc.user_name && loc.user_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (loc.address && loc.address.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <Box sx={{ width: '100%', overflowX: 'hidden', boxSizing: 'border-box', pb: 6, px: { xs: 1, md: 3 }, bgcolor: '#f4f6f8', minHeight: '100vh' }}>
      
      {/* Modern Gradient Header */}
      <Grow in={true} timeout={500}>
        <Box sx={{ 
            mb: 4, 
            mt: 2, 
            p: 4, 
            borderRadius: 4, 
            background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)', 
            color: 'white',
            boxShadow: '0 10px 30px rgba(30, 60, 114, 0.2)',
            position: 'relative',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', md: 'center' },
            gap: 3
        }}>
            <Box sx={{ position: 'relative', zIndex: 2 }}>
                <Typography variant="h4" fontWeight="800" sx={{ mb: 1, letterSpacing: 0.5, display: 'flex', alignItems: 'center', gap: 2, color: 'white' }}>
                    <LocationOnIcon sx={{ fontSize: 36, color: '#90caf9' }} />
                    Punching Assignments
                </Typography>
                <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                    Manage and track work locations and assignments
                </Typography>
            </Box>
            
            <Button 
                variant="contained" 
                startIcon={<AddIcon />} 
                onClick={() => setOpen(true)}
                sx={{ 
                    position: 'relative',
                    zIndex: 2,
                    borderRadius: 2, 
                    fontWeight: 'bold', 
                    px: 3, 
                    py: 1,
                    bgcolor: 'white',
                    color: '#1e3c72',
                    boxShadow: '0 4px 14px rgba(0,0,0,0.1)',
                    '&:hover': { bgcolor: '#f1f5f9', transform: 'translateY(-2px)', boxShadow: '0 6px 20px rgba(0,0,0,0.15)' }
                }}
            >
                New Assignment
            </Button>
            
            {/* Decorative background circles */}
            <Box sx={{ position: 'absolute', top: -50, right: -50, width: 200, height: 200, borderRadius: '50%', background: 'rgba(255,255,255,0.05)' }} />
            <Box sx={{ position: 'absolute', bottom: -100, right: 100, width: 250, height: 250, borderRadius: '50%', background: 'rgba(255,255,255,0.05)' }} />
        </Box>
      </Grow>

      {/* Filters and Controls */}
      <Card elevation={0} sx={{ mb: 4, p: 2, borderRadius: 4, border: '1px solid #f1f5f9', boxShadow: '0 4px 20px rgba(0,0,0,0.03)' }}>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'center' }, gap: 2 }}>
            <Box sx={{ flexGrow: 1 }} />
            <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' }, alignItems: 'center' }}>
                <TextField 
                    placeholder="Search User or Location..." 
                    size="small" 
                    value={searchTerm} 
                    onChange={e => setSearchTerm(e.target.value)}
                    InputProps={{
                        startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#94a3b8' }} /></InputAdornment>
                    }}
                    sx={{ 
                        flexGrow: 1, 
                        minWidth: { sm: 250 },
                        '& .MuiOutlinedInput-root': {
                            backgroundColor: '#f8fafc',
                            borderRadius: 2,
                            '& fieldset': { borderColor: '#e2e8f0' },
                            '&:hover fieldset': { borderColor: '#cbd5e1' },
                            '&.Mui-focused fieldset': { borderColor: '#3b82f6', borderWidth: '1px' },
                        }
                    }}
                />
            </Box>
        </Box>
      </Card>

      {filteredLocations.length === 0 ? (
          <Paper elevation={0} sx={{ p: 6, textAlign: 'center', border: '2px dashed #e2e8f0', borderRadius: 4, bgcolor: '#f8fafc' }}>
              <Typography color="text.secondary">No users assigned to locations yet or no matches found.</Typography>
          </Paper>
      ) : (
          <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 4, border: '1px solid #f1f5f9', overflowX: 'auto', boxShadow: '0 4px 20px rgba(0,0,0,0.03)' }}>
                <Table sx={{ minWidth: 650 }}>
                    <TableHead sx={{ bgcolor: '#f8fafc' }}>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 'bold', color: '#475569', py: 2 }}>User</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', color: '#475569', py: 2 }}>Punching ID</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', color: '#475569', py: 2 }}>Location</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', color: '#475569', py: 2 }}>Coordinates</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', color: '#475569', py: 2 }} align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredLocations.map((loc) => (
                            <TableRow 
                                key={loc.id} 
                                sx={{ cursor: 'pointer', transition: 'all 0.2s ease', '&:hover': { bgcolor: '#f1f5f9' }, '&:last-child td, &:last-child th': { border: 0 } }}
                                onClick={() => handleUserClick(loc.user_id)}
                            >
                                <TableCell>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                        <Avatar sx={{ width: 36, height: 36, background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)', fontSize: '0.9rem', fontWeight: 'bold' }}>
                                            {loc.user_name ? loc.user_name.charAt(0) : '?'}
                                        </Avatar>
                                        <Typography variant="body2" fontWeight="600" color="#1e293b">{loc.user_name}</Typography>
                                    </Box>
                                </TableCell>
                                <TableCell>
                                    <Typography variant="body2" sx={{ fontFamily: 'monospace', bgcolor: '#f1f5f9', px: 1, py: 0.5, borderRadius: 1, color: '#1e3c72', fontWeight: 'bold', display: 'inline-block' }}>
                                        {loc.userpunchingid || '—'}
                                    </Typography>
                                </TableCell>
                                <TableCell>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <LocationOnIcon sx={{ color: '#94a3b8', fontSize: 18 }} />
                                        <Typography variant="body2" color="text.secondary" fontWeight="500">{loc.address || 'No Address'}</Typography>
                                    </Box>
                                </TableCell>
                                <TableCell>
                                    <Typography variant="caption" sx={{ fontFamily: 'monospace', bgcolor: '#f8fafc', px: 1, py: 0.5, borderRadius: 1, color: '#475569', fontWeight: 'bold' }}>
                                        {loc.latitude.toFixed(6)}, {loc.longitude.toFixed(6)}
                                    </Typography>
                                </TableCell>
                                <TableCell align="right">
                                    <Button 
                                        size="small" 
                                        variant="outlined"
                                        onClick={(e) => { e.stopPropagation(); handleUserClick(loc.user_id); }}
                                        sx={{ mr: 1, borderRadius: 2, fontWeight: 'bold', minWidth: '80px', '&:hover': { bgcolor: '#eff6ff', borderColor: '#3b82f6' } }}
                                    >
                                        Details
                                    </Button>
                                    <IconButton 
                                        size="small" 
                                        sx={{ color: '#ef4444', bgcolor: '#fef2f2', '&:hover': { bgcolor: '#fee2e2' } }}
                                        onClick={(e) => { e.stopPropagation(); handleDelete(loc.id); }}
                                    >
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
          </TableContainer>
      )}

      {/* Add Dialog */}
      <Dialog 
        open={open} 
        onClose={() => setOpen(false)} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
            sx: { borderRadius: 4, boxShadow: '0 10px 40px rgba(0,0,0,0.1)' }
        }}
      >
        <DialogTitle sx={{ 
            fontWeight: '800', 
            background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)', 
            color: 'white',
            display: 'flex', 
            alignItems: 'center', 
            gap: 1.5,
            p: 3
        }}>
            <LocationOnIcon /> Assign Work Location
        </DialogTitle>
        <DialogContent dividers sx={{ p: 3, bgcolor: '#f8fafc' }}>
            <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="overline" color="text.secondary" fontWeight="800" sx={{ mb: 1, display: 'block' }}>USER</Typography>
                        <Autocomplete
                            options={users}
                            getOptionLabel={(option) => option.name || ''}
                            value={selectedUser}
                            onChange={(event, newValue) => setSelectedUser(newValue)}
                            renderInput={(params) => <TextField {...params} label="Search User" fullWidth sx={{ bgcolor: 'white' }} />}
                        />
                    </Box>
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="overline" color="text.secondary" fontWeight="800" sx={{ mb: 1, display: 'block' }}>PUNCHING ID</Typography>
                        <TextField
                            label="User Punching ID"
                            fullWidth
                            value={userPunchingId}
                            onChange={e => setUserPunchingId(e.target.value)}
                            placeholder="e.g. EMP-001"
                            sx={{ bgcolor: 'white', mb: 2 }}
                        />
                    </Box>
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="overline" color="text.secondary" fontWeight="800" sx={{ mb: 1, display: 'block' }}>LOCATION DETAILS</Typography>
                        <TextField 
                            fullWidth 
                            label="Location Name" 
                            value={address} 
                            onChange={e => setAddress(e.target.value)}
                            placeholder="e.g. Main Office, Site A"
                            sx={{ bgcolor: 'white', mb: 2 }}
                        />
                        <Box sx={{ p: 2, bgcolor: '#eff6ff', borderRadius: 2, border: '1px solid #bfdbfe' }}>
                            <Typography variant="caption" color="#2563eb" fontWeight="bold" display="block">SELECTED COORDINATES</Typography>
                            <Typography variant="body2" fontWeight="800" sx={{ mt: 0.5, color: '#1e3a8a' }}>{newLocation.lat.toFixed(6)}, {newLocation.lng.toFixed(6)}</Typography>
                            <Typography variant="caption" sx={{ mt: 1, display: 'block', color: '#3b82f6' }}>
                                Click on the map to drop a pin.
                            </Typography>
                        </Box>
                    </Box>
                </Grid>
                <Grid item xs={12} md={8}>
                    <Typography variant="overline" color="text.secondary" fontWeight="800" sx={{ mb: 1, display: 'block' }}>MAP PIN</Typography>
                    <Box sx={{ height: 400, borderRadius: 3, overflow: 'hidden', border: '1px solid #e2e8f0', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
                        <MapContainer center={[25, 45]} zoom={4} style={{ height: '100%', width: '100%' }}>
                            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                            <LocationMarker position={newLocation} setPosition={setNewLocation} />
                        </MapContainer>
                    </Box>
                </Grid>
            </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3, bgcolor: '#f8fafc', borderTop: '1px solid #e2e8f0', gap: 1 }}>
            <Button onClick={() => setOpen(false)} sx={{ color: 'text.secondary', fontWeight: 'bold' }}>Cancel</Button>
            <Button variant="contained" onClick={handleSave} disabled={loading} sx={{ borderRadius: 2, px: 3, fontWeight: 'bold' }}>
                {loading ? 'Saving...' : 'Save Assignment'}
            </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Punching;
